import { z } from "zod";

const outreachStatuses = ["Not Started", "Attempted", "Completed"] as const;

export const tcmEncounterSchema = z
  .object({
    id: z.string().uuid(),
    cxId: z.string().uuid(),
    patientId: z.string().uuid(),
    patientName: z.string(),
    patientFirstName: z.string().optional(),
    patientLastName: z.string().optional(),
    patientDateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // YYYY-MM-DD format
    patientPhoneNumbers: z.array(z.string()),
    patientStates: z.array(z.string()),
    patientFacilityIds: z.array(z.string()),
    facilityName: z.string(),
    latestEvent: z.enum(["Admitted", "Transferred", "Discharged"] as const),
    class: z.string(),
    admitTime: z
      .string()
      .datetime()
      .transform(val => new Date(val))
      .optional(),
    dischargeTime: z
      .string()
      .datetime()
      .transform(val => new Date(val))
      .nullable()
      .optional(),
    clinicalInformation: z.object({
      condition: z
        .array(
          z.object({
            coding: z.array(
              z.object({
                system: z.string(),
                code: z.string(),
                display: z.string(),
              })
            ),
          })
        )
        .optional(),
    }),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    version: z.number().optional(),
    freetextNote: z.string().optional(),
    dischargeSummaryPath: z.string().nullable().optional(),
    outreachStatus: z.enum(outreachStatuses).default("Not Started"),
    lastOutreachDate: z
      .string()
      .datetime()
      .nullish()
      .transform(val => (val ? new Date(val) : null)),
    outreachLogs: z
      .array(
        z.object({
          status: z.enum(["Attempted", "Completed"] as const),
          timestamp: z.string().datetime(),
        })
      )
      .default([]),
    hasCardiacCode: z.boolean().optional(),
    eTag: z.string().optional(),
    externalUrls: z.array(
      z.object({
        url: z.string(),
        source: z.string(),
      })
    ),
  })
  .strict();
export type TcmEncounter = z.infer<typeof tcmEncounterSchema>;

export const tcmEncounterResponseSchema = z
  .object({
    items: z.array(tcmEncounterSchema),
    meta: z.object({
      itemsOnPage: z.number(),
      itemsInTotal: z.number().optional(),
      prevPage: z.string().optional(),
      nextPage: z.string().optional(),
    }),
  })
  .strict();
export type TcmEncounterResponse = z.infer<typeof tcmEncounterResponseSchema>;

export const updateTcmEncounterResponseSchema = tcmEncounterSchema.partial();
export type UpdateTcmEncounterResponse = z.infer<typeof updateTcmEncounterResponseSchema>;
export { outreachStatuses };
